import React from 'react';
import { useTranslation } from 'react-i18next';
import { graphql } from 'gatsby';
import Img from 'gatsby-image/withIEPolyfill';

import { GatsbySanityImageFluid } from '../types';
import GenericLink from './GenericLink';

import * as styles from './ContactBlock.module.scss';

export interface Contact {
  position?: string | null;
  name: string;
  image?: {
    asset: {
      fluid: GatsbySanityImageFluid;
    };
  } | null;
  telephones?: Array<string> | null;
  mobilePhones?: Array<string> | null;
  fax?: string | null;
  email?: string | null;
  address?: {
    street?: string | null;
    zipCode?: string | null;
    city?: string | null;
  } | null;
}

function cleanContact({
  position,
  name,
  image,
  telephones,
  mobilePhones,
  fax,
  email,
  address,
}: Contact) {
  position = position || '';
  telephones = telephones || [];
  mobilePhones = mobilePhones || [];
  fax = fax || '';
  email = email || '';
  address = address || {};

  return {
    position: position.trim(),
    name,
    image,
    telephones: telephones.map(telephone => telephone.trim()).filter(Boolean),
    mobilePhones: mobilePhones.map(mobilePhone => mobilePhone.trim()).filter(Boolean),
    fax: fax.trim(),
    email: email.trim(),
    address: {
      street: address.street ? address.street.trim() : '',
      zipCode: address.zipCode ? address.zipCode.trim() : '',
      city: address.city ? address.city.trim() : '',
    },
  };
}

export function canHaveNameBesideImage(contact: Contact): boolean {
  const { telephones, mobilePhones, fax, email } = cleanContact(contact);
  let nEntries = 0;
  nEntries += telephones.length;
  nEntries += mobilePhones.length;
  nEntries += fax ? 1 : 0;
  nEntries += email ? 2 : 0;
  return nEntries <= 2;
}

export const query = graphql`
  fragment Contact on SanityContact {
    position
    name
    image {
      asset {
        fluid(maxWidth: 200, maxHeight: 200) {
          ...GatsbySanityImageFluid
        }
      }
    }
    telephones
    mobilePhones
    fax
    email
    address {
      street
      zipCode
      city
    }
  }
`;

interface ContactBlockProps {
  contact: Contact;
  isCompany?: boolean;
  nameBesideImage?: boolean;
}

export default function ContactBlock({
  contact,
  isCompany = false,
  nameBesideImage = false,
}: ContactBlockProps): React.ReactElement {
  const { t } = useTranslation();
  const { position, name, image, telephones, mobilePhones, fax, email, address } =
    cleanContact(contact);

  const nameRow = [
    {
      key: 'name',
      label: isCompany
        ? t('contact_card.company_name_label', 'Company Name')
        : position || undefined,
      value: name,
      valueStr: name,
      isValueName: true,
    },
  ];

  const rows = [];
  let nextRow = [];

  if (nameBesideImage) {
    rows.push(nameRow);
  }

  for (const [i, telephone] of telephones.entries()) {
    nextRow.push({
      key: 'phone-' + i,
      label: t('contact_card.telephone_label', 'Telephone'),
      value: (
        <GenericLink className={styles.infoContact} noExternalUrlIcon to={'tel:' + telephone}>
          {telephone}
        </GenericLink>
      ),
      valueStr: telephone,
    });
    if (nextRow.length === 2) {
      rows.push(nextRow);
      nextRow = [];
    }
  }

  for (const [i, mobilePhone] of mobilePhones.entries()) {
    nextRow.push({
      key: 'mobile-' + i,
      label: t('contact_card.mobile_label', 'Mobile'),
      value: (
        <GenericLink className={styles.infoContact} noExternalUrlIcon to={'tel:' + mobilePhone}>
          {mobilePhone}
        </GenericLink>
      ),
      valueStr: mobilePhone,
    });
    if (nextRow.length === 2) {
      rows.push(nextRow);
      nextRow = [];
    }
  }

  if (fax) {
    nextRow.push({
      key: 'fax',
      label: t('contact_card.fax_label', 'Fax'),
      value: (
        <GenericLink className={styles.infoContact} noExternalUrlIcon to={'fax:' + fax}>
          {fax}
        </GenericLink>
      ),
      valueStr: fax,
    });
    if (nextRow.length === 2) {
      rows.push(nextRow);
      nextRow = [];
    }
  }

  if (nextRow.length === 1) {
    rows.push(nextRow);
    nextRow = [];
  }

  const addressStr = [address.street, address.zipCode, address.city].filter(Boolean).join(' ');

  function renderRow(
    row: Array<{
      key: string;
      label?: string;
      value: string | React.ReactElement;
      valueStr: string;
      isValueName?: boolean;
      isValueMultiline?: boolean;
      hidden?: boolean;
    }>,
  ) {
    return (
      <div
        key={row.map(entry => entry.key).join(',')}
        className={
          styles.infoRow +
          (row.length === 1 ? ' ' + styles.singleEntryRow : '') +
          (row.every(entry => entry.hidden) ? ' ' + styles.hidden : '')
        }
      >
        {row.map((entry, j) => (
          <div key={j} className={styles.infoBlock}>
            <span
              className={styles.label + (!entry.label ? ' ' + styles.hidden : '')}
              title={entry.label}
            >
              {entry.label || 'X'}
            </span>

            {entry.isValueName ? (
              <h4
                className={styles.info + (entry.isValueMultiline ? ' ' + styles.infoMultiline : '')}
                title={entry.valueStr}
              >
                {entry.value}
              </h4>
            ) : (
              <span
                className={styles.info + (entry.isValueMultiline ? ' ' + styles.infoMultiline : '')}
                title={entry.valueStr}
              >
                {entry.value}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {!nameBesideImage && renderRow(nameRow)}
      <div className={styles.imageAndInfosBox}>
        {!!(image && image.asset) && (
          <Img className={styles.image} fluid={image.asset.fluid} alt={name} />
        )}
        <div className={styles.infosSideBox}>
          {[
            ...rows.slice(0, 2),
            [{ key: 'mock-1', label: 'X', value: 'X', valueStr: 'X', hidden: true }],
            [{ key: 'mock-2', label: 'X', value: 'X', valueStr: 'X', hidden: true }],
          ]
            .slice(0, 2)
            .map(row => renderRow(row))}
        </div>
      </div>
      {rows.slice(2).map(row => renderRow(row))}
      {!!email &&
        renderRow([
          {
            key: 'email',
            label: t('contact_card.email_label', 'Email'),
            value: (
              <GenericLink className={styles.infoContact} noExternalUrlIcon to={'mailto:' + email}>
                {email}
              </GenericLink>
            ),
            valueStr: email,
            isValueMultiline: true,
          },
        ])}
      {(!!address.street || !!address.zipCode || !!address.city) &&
        renderRow([
          {
            key: 'address',
            label: t('contact_card.address_label', 'Address'),
            value: (
              <GenericLink
                className={styles.infoContact}
                noExternalUrlIcon
                to={'https://www.google.com/maps/search/?api=1&query=' + encodeURI(addressStr)}
              >
                {addressStr}
              </GenericLink>
            ),
            valueStr: addressStr,
            isValueMultiline: true,
          },
        ])}
    </div>
  );
}
